// src/components/Register.js
import React, { useState, useEffect } from 'react';
import { firestore } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc
} from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import { getFreshlingDisplayName } from '../utils/flyerDisplay';
import { getPublicDefaultEventId } from '../utils/activeEvent';
import {
  isFmMentorPairingExcluded,
  isRegistrationMentorApproved,
} from '../utils/mentorEligibility';

import './Register.css';

const Register = () => {
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('event') || searchParams.get('eventId');
  const [activeEvent, setActiveEvent] = useState(null);
  const [mentorEmail, setMentorEmail] = useState('');
  const [mentorRecord, setMentorRecord] = useState(null);
  const [teamData, setTeamData] = useState({ teamName: '', division: 'Intermediate' });
  const [existingTeam, setExistingTeam] = useState(null);
  const [freshlingNames, setFreshlingNames] = useState([]); // display names for team freshlings
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Fetch the active event (assumes exactly one active event)
  useEffect(() => {
    async function fetchActiveEvent() {
      try {
        if (eventIdParam) {
          const snap = await getDoc(doc(firestore, 'events', eventIdParam));
          if (snap.exists()) {
            setActiveEvent({ id: snap.id, ...snap.data() });
          } else {
            setError('Requested event not found.');
          }
        } else {
          const id = await getPublicDefaultEventId();
          if (!id) {
            setError('No active event found.');
            return;
          }
          const snap = await getDoc(doc(firestore, 'events', id));
          if (snap.exists()) {
            setActiveEvent({ id: snap.id, ...snap.data() });
          } else {
            setError('No active event found.');
          }
        }
      } catch (err) {
        setError('Error fetching active event: ' + err.message);
      }
    }
    fetchActiveEvent();
  }, [eventIdParam]);

  // When mentorRecord is set, check if a team exists for this mentor for the active event.
  useEffect(() => {
    async function fetchTeam() {
      if (activeEvent && mentorRecord) {
        try {
          const q = query(
            collection(firestore, 'teams'),
            where('eventId', '==', activeEvent.id),
            where('mentor', '==', mentorRecord.email)
          );
          const qs = await getDocs(q);
          if (!qs.empty) {
            const teamDoc = qs.docs[0];
            const team = { id: teamDoc.id, ...teamDoc.data() };
            setExistingTeam(team);
            setTeamData({
              teamName: team.teamName || '',
              division: team.division || 'Intermediate'
            });
            setFreshlingNames([]);
          } else {
            setExistingTeam(null);
            setFreshlingNames([]);
          }
        } catch (err) {
          console.error('Error fetching team:', err);
        }
      }
    }
    fetchTeam();
  }, [activeEvent, mentorRecord]);

  // When existingTeam has freshlings, fetch flyer records to get display names.
  useEffect(() => {
    async function fetchFreshlingNames() {
      const emails = Array.isArray(existingTeam?.freshlings) ? existingTeam.freshlings : [];
      if (!activeEvent?.id || emails.length === 0) {
        setFreshlingNames([]);
        return;
      }
      try {
        const q = query(
          collection(firestore, 'flyers'),
          where('eventId', '==', activeEvent.id),
          where('type', '==', 'freshling')
        );
        const qs = await getDocs(q);
        const emailLower = (e) => (e || '').trim().toLowerCase();
        const names = [];
        emails.forEach((email) => {
          const flyerDoc = qs.docs.find((d) => emailLower(d.data().email) === emailLower(email));
          const flyer = flyerDoc ? flyerDoc.data() : null;
          names.push(flyer ? getFreshlingDisplayName(flyer) : email);
        });
        setFreshlingNames(names);
      } catch (err) {
        console.error('Error fetching freshling names:', err);
        setFreshlingNames(emails);
      }
    }
    fetchFreshlingNames();
  }, [activeEvent?.id, existingTeam]);

  // Handler for mentor email submission
  const handleMentorEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!activeEvent) {
        setError('No active event available.');
        setLoading(false);
        return;
      }
      // Query flyers for this active event for mentors
      // We fetch all mentors and do case-insensitive matching since emails might be stored with mixed case
      const q = query(
        collection(firestore, 'flyers'),
        where('eventId', '==', activeEvent.id),
        where('type', '==', 'mentor')
      );
      const qs = await getDocs(q);
      
      // Normalize the input email for comparison (trim and lowercase)
      const normalizedInputEmail = mentorEmail.trim().toLowerCase();
      
      // Find matching mentor with case-insensitive email comparison
      let mentorDoc = null;
      qs.forEach((doc) => {
        const flyerData = doc.data();
        const normalizedFlyerEmail = (flyerData.email || '').trim().toLowerCase();
        if (normalizedFlyerEmail === normalizedInputEmail) {
          mentorDoc = doc;
        }
      });
      
      if (!mentorDoc) {
        setError(
          "Mentor email not found. Please use the mentor email you registered with, or contact christy@furycoaching.com for help."
        );
        setLoading(false);
        return;
      }

      const mentorData = { id: mentorDoc.id, ...mentorDoc.data() };
      if (mentorData.cancelled) {
        setError(
          'This mentor registration is cancelled. Contact christy@furycoaching.com if you believe this is a mistake.'
        );
        setLoading(false);
        return;
      }
      if (mentorData.waiting) {
        setError(
          'Mentors on the waiting list cannot register a team here yet. Contact christy@furycoaching.com for help.'
        );
        setLoading(false);
        return;
      }
      if (isFmMentorPairingExcluded(mentorData)) {
        setError(
          'This mentor email is not set up for team registration in Fresh Meet. Contact christy@furycoaching.com for help.'
        );
        setLoading(false);
        return;
      }
      if (!isRegistrationMentorApproved(mentorData)) {
        setError(
          'Your mentor application is not staff-approved in registration yet. You can register your team here once you appear as an approved mentor.'
        );
        setLoading(false);
        return;
      }

      setMentorRecord(mentorData);
    } catch (err) {
      setError('Error verifying mentor email: ' + err.message);
    }
    setLoading(false);
  };

  // Handler for saving registration (creating/updating team)
  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (!activeEvent || !mentorRecord) return;
    const teamPayload = {
      teamName: teamData.teamName,
      division: teamData.division,
      mentor: mentorRecord.email,
      eventId: activeEvent.id
    };
    try {
      if (existingTeam) {
        // Update existing team
        const teamRef = doc(firestore, 'teams', existingTeam.id);
        await updateDoc(teamRef, teamPayload);
        alert('Team information updated.');
      } else {
        // Create a new team
        await addDoc(collection(firestore, 'teams'), teamPayload);
        alert('Team registered successfully.');
      }
      // Redirect after alert closes so nothing (e.g. service worker) can intercept
      setTimeout(() => {
        window.location.replace('https://furycoaching.com');
      }, 0);
      return;
    } catch (err) {
      setError('Error saving registration: ' + err.message);
    }
  };

  if (loading) return <p>Loading...</p>;

  const registrationClosed = activeEvent && (!activeEvent.acceptingTeamNames || activeEvent.isOpen === false);

  return (
    <div className="register-page">
      <h2>Team Registration</h2>
      {registrationClosed ? (
        <div className="event-closed-banner" style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fca5a5',
          borderRadius: '8px',
          padding: '16px 20px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#991b1b', fontWeight: 600, fontSize: '1.05rem', margin: 0 }}>
            Registration is closed for this event.
          </p>
        </div>
      ) : !mentorRecord ? (
        <form onSubmit={handleMentorEmailSubmit} className="mentor-email-form">
          <label htmlFor="mentorEmail">Enter your Mentor Email:</label>
          <input
            id="mentorEmail"
            type="email"
            value={mentorEmail}
            onChange={(e) => setMentorEmail(e.target.value)}
            onBlur={(e) => setMentorEmail(e.target.value.trim())}
            required
          />
          <button type="submit" className="btn-fm-primary">Verify Email</button>
          {error && <p className="error">{error}</p>}
        </form>
      ) : (
        <div className="registration-form">
          <h3>
            Mentor: {mentorRecord.name || mentorRecord.email}
          </h3>
          {freshlingNames.length > 0 && (
            <p className="register-team-list">
              Team: {freshlingNames.join(', ')}
            </p>
          )}
          <form onSubmit={handleRegistrationSubmit}>
            <div className="form-group">
              <label>Team Name:</label>
              <input
                type="text"
                value={teamData.teamName}
                onChange={(e) => setTeamData({ ...teamData, teamName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Division:</label>
              <select
                value={teamData.division}
                onChange={(e) => setTeamData({ ...teamData, division: e.target.value })}
                required
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
              </select>
            </div>
            <button type="submit">Save Registration</button>
            {error && <p className="error">{error}</p>}
          </form>
        </div>
      )}
    </div>
  );
};

export default Register;
